import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { ConditionOutcome } from 'components/powerFlowShared/components/types';
import { flowNodeColor } from 'fable/flowEditor';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type { PhoneExtraData } from 'flow-builder/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { Locations as LocationsModal } from '../../modals/locations';
import type { Payload } from '../locations';

export type LocationsNodeAttributes = GenericNodeAttributes<
  Payload,
  PhoneExtraData
>;
export type LocationsNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: LocationsNodeAttributes['translatePayload'] = (
  node,
) => {
  const { rules, non_match_node_id } = node.payload;
  const connections = rules.map((rule) => ({
    id: uuid(),
    connection: rule.match_node_id || uuid(),
    label: rule.name,
  }));

  return [
    {
      id: uuid(),
      connection: non_match_node_id || uuid(),
      label: ConditionOutcome.NO_MATCH,
    },
    ...connections,
  ];
};

const updateConnection: LocationsNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const payload = { ...node.payload };
  const rules = [...payload.rules];

  if (connection.id === payload.non_match_node_id) {
    payload.non_match_node_id = connectionId;
  } else {
    const index = rules.findIndex(
      ({ match_node_id }) => connection.id === match_node_id,
    );

    if (index >= 0) {
      rules[index] = {
        ...rules[index],
        match_node_id: connectionId,
      };
    }
  }

  return { ...node, payload: { ...payload, rules } };
};

export const LocationsNodeAttributes: LocationsNodeAttributes = {
  iconName: 'world',
  label: 'Locations',
  helpText:
    'Sort incoming calls based on country code. This is useful for general and specific locations (e.g. +44 for UK or +49035 for Dresden in Germany).',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: LocationsNode,
  ActionModal: LocationsModal,
};

export function LocationsNode({ node }: LocationsNodeProps): JSX.Element {
  const { payload } = node;
  const { rules } = payload as Payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text
              weight="semibold"
              text={`${rules.length} ${
                rules.length === 1 ? 'location' : 'locations'
              }`}
            />
          </TextEllipsis>
        ),
        [rules.length],
      )}
    </TextContent>
  );
}
